import React, { useEffect, useState } from "react";
import { Box, Typography, Link, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import VideocamIcon from "@mui/icons-material/Videocam";
import DescriptionIcon from "@mui/icons-material/Description";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import ChatMediaCollapsedPreview from "../Inbox/Features/ChatBoxFeatures/ChatMediaCollapsedPreview";
import AudioBox from "../Inbox/Features/ChatBoxFeatures/AudioBox";

const getBlurPlaceholderUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  if (url.includes("cloudinary.com")) {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}q=auto:low&w=200&e_blur:300&f_auto`;
  }
  return null;
};

function basenameFromUrl(url) {
  try {
    const parts = String(url).split(/[\\/]/);
    let name = parts[parts.length - 1]?.split("?")[0] || "file";
    return decodeURIComponent(name);
  } catch {
    return "file";
  }
}

/** Image: collapsed blurred preview → click loads full image (zoom via react-photo-view). */
function TaskChatImageBody({ url, outgoingLight }) {
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setRevealed(false);
    setLoading(false);
    setError(false);
  }, [url]);

  const placeholderUrl = getBlurPlaceholderUrl(url);
  const activate = () => {
    setLoading(true);
    setRevealed(true);
  };

  if (!revealed) {
    return (
      <ChatMediaCollapsedPreview
        thumbnailUrl={placeholderUrl}
        fallbackSrc={null}
        sizeBytes={null}
        onActivate={activate}
        minHeight={150}
        maxHeight={200}
      />
    );
  }

  if (error) {
    return (
      <Typography variant="body2" sx={{ opacity: 0.85, color: outgoingLight ? "common.white" : undefined }}>
        Failed to load image
      </Typography>
    );
  }

  return (
    <PhotoProvider>
      <PhotoView src={url}>
        <Box
          sx={{
            position: "relative",
            maxWidth: "calc(var(--task-chat-width, 100vw) * 0.5)",
            mt: 0.5,
            cursor: "pointer",
          }}
        >
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}
          <Box
            component="img"
            src={url}
            alt=""
            onLoad={() => {
              setLoading(false);
              setError(false);
            }}
            onError={() => {
              setLoading(false);
              setError(true);
            }}
            sx={{
              maxWidth: "100%",
              maxHeight: 280,
              borderRadius: 1,
              display: "block",
              objectFit: "contain",
            }}
          />
        </Box>
      </PhotoView>
    </PhotoProvider>
  );
}

function TaskChatVideoBody({ url }) {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    setRevealed(false);
  }, [url]);

  if (!revealed) {
    return (
      <ChatMediaCollapsedPreview
        thumbnailUrl={null}
        fallbackSrc={null}
        sizeBytes={null}
        onActivate={() => setRevealed(true)}
        minHeight={150}
        maxHeight={200}
        bottomLeft={
          <>
            <VideocamIcon sx={{ fontSize: 16, opacity: 0.95 }} />
            <Typography component="span" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
              Video
            </Typography>
          </>
        }
      />
    );
  }

  return (
    <Box sx={{ mt: 0.5, maxWidth: "calc(var(--task-chat-width, 100vw) * 0.5)" }}>
      <Box component="video" src={url} controls playsInline sx={{ width: "100%", maxHeight: 240, borderRadius: 1 }} />
    </Box>
  );
}

function TaskChatDocumentBody({ url, documentFileName }) {
  const name = documentFileName?.trim() || basenameFromUrl(url);
  const extMatch = String(name).match(/\.([a-zA-Z0-9]+)(\?|$)/);
  const extLabel = extMatch ? extMatch[1].toUpperCase() : "FILE";

  const openDocument = () => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      sx={{
        maxWidth: "min(280px, calc(var(--task-chat-width, 100vw) * 0.85))",
        borderRadius: "12px",
        overflow: "hidden",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: "background.paper",
        mt: 0.5,
      }}
    >
      <Box sx={{ position: "relative", minHeight: 130 }}>
        <ChatMediaCollapsedPreview
          thumbnailUrl={null}
          fallbackSrc={null}
          sizeBytes={null}
          onActivate={openDocument}
          minHeight={130}
          maxHeight={160}
          borderRadius="0"
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          px: 1.5,
          py: 1.1,
          bgcolor: (theme) => (theme.palette.mode === "dark" ? "grey.900" : "#fff"),
        }}
      >
        <DescriptionIcon sx={{ fontSize: 22, color: "primary.main", flexShrink: 0, mt: 0.25 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              wordBreak: "break-word",
              fontSize: "0.9375rem",
            }}
          >
            {name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            {extLabel}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function renderTaskMessageWithMentions(text, mentionColor, whiteMentions = false) {
  if (!text || typeof text !== "string" || !text.includes("@")) return text;

  const parts = [];
  let lastIndex = 0;
  let mentionIndex = 0;

  while ((mentionIndex = text.indexOf("@", mentionIndex)) !== -1) {
    if (mentionIndex > lastIndex) {
      parts.push({ type: "text", content: text.substring(lastIndex, mentionIndex) });
    }

    let endIndex = mentionIndex + 1;
    while (endIndex < text.length) {
      const char = text[endIndex];

      if (char === " ") {
        let nextCharIndex = endIndex + 1;
        while (nextCharIndex < text.length && text[nextCharIndex] === " ") {
          nextCharIndex++;
        }

        if (nextCharIndex >= text.length) {
          endIndex = text.length;
          break;
        }

        if (/[a-z]/.test(text[nextCharIndex])) {
          break;
        }

        endIndex += 1;
      } else {
        endIndex++;
      }
    }

    parts.push({ type: "mention", content: text.substring(mentionIndex, endIndex) });
    lastIndex = endIndex;
    mentionIndex = endIndex + 1;
  }

  if (lastIndex < text.length) {
    parts.push({ type: "text", content: text.substring(lastIndex) });
  }

  return parts.map((part, index) =>
    part.type === "mention" ? (
      <Box
        key={index}
        component="span"
        sx={{
          color: whiteMentions ? "#fff !important" : mentionColor,
          fontWeight: whiteMentions ? 600 : 500,
        }}
      >
        {part.content}
      </Box>
    ) : (
      <React.Fragment key={index}>{part.content}</React.Fragment>
    )
  );
}

/**
 * Rich media bubbles for task chat (collapsed previews; tap to load / open like inbox).
 */
export default function TaskChatMessageMedia({ msg, fromMe }) {
  const theme = useTheme();
  const body = msg.body || "";
  const isUrl = /^https?:\/\//i.test(body);
  const isDark = theme.palette.mode === "dark";
  // Outgoing bubbles now use the light-green Inbox style (dark text) in both modes.
  // Light text is only needed for the incoming dark-mode (grey) bubble.
  const lightText = !fromMe && isDark;
  const whiteMentions = lightText;
  const mentionColor = whiteMentions ? "#ffffff" : "#0d47a1";
  const outgoingTextSx = lightText
    ? {
        color: "#fff !important",
        "&, & *": { color: "#fff !important" },
      }
    : {};

  if (!msg.messageType || msg.messageType === "text") {
    return (
      <Typography
        variant="body2"
        sx={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontSize: "0.9375rem",
          lineHeight: 1.45,
          fontWeight: fromMe ? 500 : 400,
          ...outgoingTextSx,
        }}
      >
        {renderTaskMessageWithMentions(body, mentionColor, whiteMentions)}
      </Typography>
    );
  }

  if (!isUrl) {
    return (
      <Typography
        variant="body2"
        sx={{
          opacity: 0.85,
          fontSize: "0.9375rem",
          ...outgoingTextSx,
        }}
      >
        {renderTaskMessageWithMentions(body || "(Unsupported attachment)", mentionColor, whiteMentions)}
      </Typography>
    );
  }

  if (msg.messageType === "image") {
    return <TaskChatImageBody url={body} outgoingLight={lightText} />;
  }

  if (msg.messageType === "video") {
    return <TaskChatVideoBody url={body} />;
  }

  if (msg.messageType === "audio") {
    return (
      <Box sx={{ mt: 0.25 }}>
        <AudioBox
          audioUrl={body}
          initialDurationSeconds={msg.audioDurationSeconds}
          mediaFileSizeBytes={msg.mediaFileSizeBytes}
          fromMe={fromMe}
        />
      </Box>
    );
  }

  if (msg.messageType === "document") {
    return <TaskChatDocumentBody url={body} documentFileName={msg.documentFileName} />;
  }

  const label = msg.documentFileName || basenameFromUrl(body);
  return (
    <Typography variant="body2" sx={{ mt: 0.25 }}>
      <Link
        href={body}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          fontSize: "0.9375rem",
          wordBreak: "break-word",
          color: lightText ? "common.white !important" : undefined,
          fontWeight: 600,
          textDecorationColor: lightText ? "rgba(255,255,255,0.45)" : undefined,
        }}
      >
        {label}
      </Link>
    </Typography>
  );
}
